import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, SparklesIcon } from 'lucide-react';
type PricingTier = {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  featured?: boolean;
  cta: string;
};
const tiers: PricingTier[] = [{
  name: 'Starter',
  price: '$0',
  period: '/month',
  description: 'Perfect for exploring the holographic design system.',
  features: ['Core glass components', 'Basic gradient utilities', 'Community support', '1 project'],
  cta: 'Get Started Free'
}, {
  name: 'Pro',
  price: '$29',
  period: '/month',
  description: 'Full access to all holographic effects and animations.',
  features: ['All Starter features', 'Advanced 3D transforms', 'Custom gradient builder', 'Priority support', 'Unlimited projects', 'Figma plugin access'],
  featured: true,
  cta: 'Start Pro Trial'
}, {
  name: 'Enterprise',
  price: 'Custom',
  period: '',
  description: 'Tailored solutions for large-scale implementations.',
  features: ['All Pro features', 'Custom component development', 'Dedicated support engineer', 'SLA guarantee', 'White-label licensing', 'On-premise deployment'],
  cta: 'Contact Sales'
}];
type PricingCardProps = {
  tier: PricingTier;
  index: number;
};
function PricingCard({
  tier,
  index
}: PricingCardProps) {
  return <motion.article initial={{
    opacity: 0,
    y: 30,
    filter: 'blur(10px)'
  }} whileInView={{
    opacity: 1,
    y: 0,
    filter: 'blur(0px)'
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.6,
    delay: index * 0.15,
    ease: [0.16, 1, 0.3, 1]
  }} className={`relative ${tier.featured ? 'md:-mt-4 md:mb-4' : ''}`} data-id="element-75">
      {/* Featured Badge */}
      {tier.featured && <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20" data-id="element-76">
          <div className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-medium shadow-lg shadow-purple-500/30" data-id="element-77">
            <SparklesIcon className="w-3.5 h-3.5" data-id="element-78" />
            <span data-id="element-79">Most Popular</span>
          </div>
        </div>}

      <div className={`h-full ${tier.featured ? 'gradient-border gradient-border-thick' : ''} rounded-2xl`} data-id="element-80">
        <div className={`
            glass rounded-2xl p-8 h-full flex flex-col relative overflow-hidden
            ${tier.featured ? 'bg-gradient-to-b from-blue-500/10 to-purple-500/10' : ''}
          `} data-id="element-81">
          {/* Glow Effect for Featured */}
          {tier.featured && <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" aria-hidden="true" data-id="element-82" />}

          {/* Header */}
          <div className="relative z-10 mb-8" data-id="element-83">
            <h3 className="text-xl font-semibold text-white mb-2" data-id="element-84">
              {tier.name}
            </h3>
            <div className="flex items-baseline gap-1 mb-3" data-id="element-85">
              <span className={`text-4xl font-bold ${tier.featured ? 'text-holo' : 'text-white'}`} data-id="element-86">
                {tier.price}
              </span>
              {tier.period && <span className="text-gray-500" data-id="element-87">{tier.period}</span>}
            </div>
            <p className="text-gray-400 text-sm" data-id="element-88">{tier.description}</p>
          </div>

          {/* Features */}
          <ul className="relative z-10 space-y-4 mb-8 flex-1" role="list" data-id="element-89">
            {tier.features.map((feature, featureIndex) => <li key={featureIndex} className="flex items-start gap-3" data-id="element-90">
                <div className={`
                  w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5
                  ${tier.featured ? 'bg-gradient-to-r from-blue-500 to-purple-500' : 'bg-white/10'}
                `} data-id="element-91">
                  <CheckIcon className="w-3 h-3 text-white" data-id="element-92" />
                </div>
                <span className="text-gray-300 text-sm" data-id="element-93">{feature}</span>
              </li>)}
          </ul>

          {/* CTA */}
          <button className={`
              relative z-10 w-full py-3.5 px-6 rounded-xl font-medium transition-all duration-300
              ${tier.featured ? 'btn-glass-primary hover:shadow-lg hover:shadow-purple-500/30' : 'btn-glass'}
            `} data-id="element-94">
            {tier.cta}
          </button>
        </div>
      </div>
    </motion.article>;
}
export function PricingSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, {
    once: true,
    margin: '-100px'
  });
  return <section ref={sectionRef} className="relative py-32 px-4" aria-labelledby="pricing-heading" data-id="element-95">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/[0.02] to-transparent" aria-hidden="true" data-id="element-96" />

      {/* Decorative Orbs */}
      <div className="orb orb-purple w-[300px] h-[300px] top-0 right-0 opacity-20" aria-hidden="true" data-id="element-97" />
      <div className="orb orb-blue w-[200px] h-[200px] bottom-20 left-10 opacity-20" aria-hidden="true" data-id="element-98" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-99">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-100">
          <motion.span initial={{
          opacity: 0,
          scale: 0.9
        }} animate={isInView ? {
          opacity: 1,
          scale: 1
        } : {}} transition={{
          duration: 0.5
        }} className="inline-block px-4 py-1.5 rounded-full glass text-sm text-purple-400 mb-4" data-id="element-101">
            Pricing
          </motion.span>
          <h2 id="pricing-heading" className="text-3xl md:text-5xl font-bold mb-4" data-id="element-102">
            <span className="text-white" data-id="element-103">Choose Your </span>
            <span className="text-holo" data-id="element-104">Plan</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-105">
            Start free and scale as you grow. All plans include our core
            holographic design system.
          </p>
        </motion.div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start" data-id="element-106">
          {tiers.map((tier, index) => <PricingCard key={tier.name} tier={tier} index={index} data-id="element-107" />)}
        </div>

        {/* Footer Note */}
        <motion.p initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        delay: 0.8,
        duration: 0.6
      }} className="text-center text-gray-500 text-sm mt-12" data-id="element-108">
          All plans include a 14-day free trial. No credit card required.
        </motion.p>
      </div>
    </section>;
}